import { Warehouse as WarehouseIcon } from "lucide-react";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useEffect, useState } from 'react';
import { Button } from 'ui/button';
import { Input } from 'ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'ui/card';
import { Badge } from 'ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui/select';
import { Label } from 'ui/label';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  MapPin, 
  Database,
  CheckCircle2,
  Filter,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';


interface Bin {
  id: string;
  name: string;
  displayName?: string; // Optional manual override
  code: string;
  status: 'active' | 'inactive' | 'deprecated';
}

interface Warehouse {
  id: string;
  name: string;
  displayName?: string; // Optional manual override
  code: string;
  dialCode?: string;
  warehouseCode?: string;
  bins: Bin[];
  status: 'active' | 'inactive';
}

// Comprehensive list of world warehouses with ISO codes
const WORLD_WAREHOUSES = [
  {
    name: 'Karbala Warehouse',
    code: 'WH-KRB',
    iso2: 'KRB',
    iso3: 'KARBALA',
    dialCode: 'Karbala', // ✅ Venue
  },
  {
    name: 'Najaf Warehouse',
    code: 'WH-NJF',
    iso2: 'NJF',
    iso3: 'NAJAF',
    dialCode: 'Najaf', // ✅ Venue
  },
  {
    name: 'Baghdad Warehouse',
    code: 'WH-BGD',
    iso2: 'BGD',
    iso3: 'BAGHDAD',
    dialCode: 'Baghdad',
  }
].sort((a, b) => a.name.localeCompare(b.name));

// Mock bins database by warehouse code
const BINS_BY_WAREHOUSE: Record<string, readonly string[]> = {
  'WH-KRB': [
    'Bin A2',
    'Bin B1',
  ] as const,
  'WH-NJF': [
    'Bin N1',
    'Bin N2',
  ] as const,
  'WH-BGD': [
    'Bin Main',
    'Bin Secondary',
  ] as const,
}

export const WarehouseMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Warehouse Master",
      childTitle: "Manage venues and their configurations",
      icon: <WarehouseIcon className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddBinDialogOpen, setIsAddBinDialogOpen] = useState(false);
  const [isEditWarehouseDialogOpen, setIsEditWarehouseDialogOpen] = useState(false);
  const [isEditBinDialogOpen, setIsEditBinDialogOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [editingBin, setEditingBin] = useState<Bin | null>(null);
  const [sortField, setSortField] = useState<'name' | 'code' | 'bins' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isAddWarehouseDialogOpen, setIsAddWarehouseDialogOpen] = useState(false);

  const [addWarehouseFormData, setAddWarehouseFormData] = useState({
    name: '',
    code: '',
    dialCode: '', // venue
    countryCode: '',
    status: 'active' as 'active' | 'inactive',
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [deleteType, setDeleteType] = useState<'bin' | 'warehouse' | null>(null);

  const [deletingItem, setDeletingItem] = useState<{
    id: string;
    name: string;
    warehouseId?: string;
  } | null>(null);

  // Initialize all warehouses from WORLD_WAREHOUSES
  const [warehouses, setWarehouses] = useState<Warehouse[]>(() => {
    return WORLD_WAREHOUSES.map((warehouse, index) => ({
      id: `warehouse-${index + 1}`,
      name: warehouse.name,
      code: warehouse.code,
      dialCode: warehouse.dialCode,
      warehouseCode: `${warehouse.iso2} / ${warehouse.iso3}`,
      status: 'active' as const,
      bins: (BINS_BY_WAREHOUSE[warehouse.code] || []).map((binName: string, binIndex: number) => ({
        id: `${warehouse.code}-${binIndex + 1}`,
        name: binName,
        code: binName.substring(0, 3).toUpperCase(),
        status: 'active' as const
      }))
    }));
  });

  // Form state for adding bin
  const [binFormData, setBinFormData] = useState({
    binName: '',
    binCode: '',
    status: 'active' as 'active' | 'inactive' | 'deprecated',
  });

  // Form state for editing warehouse
  const [editWarehouseFormData, setEditWarehouseFormData] = useState({
    selectedWarehouse: '',
    name: '',
    displayName: '',
    code: '',
    dialCode: '',
    warehouseCode: '',
    status: 'active' as 'active' | 'inactive',
  });

  // Form state for editing bin
  const [editBinFormData, setEditBinFormData] = useState({
    name: '',
    code: '',
    status: 'active' as 'active' | 'inactive' | 'deprecated',
  });

  // Advanced Filters State
  const [filters, setFilters] = useState({
    warehouseStatus: 'all' as 'all' | 'active' | 'inactive',
    binStatus: 'all' as 'all' | 'active' | 'inactive' | 'deprecated',
    warehouseCode: '',
    warehouseName: '',
    hasNoBins: false,
  });
  const [showFilters, setShowFilters] = useState(false);

  // Apply both search and advanced filters
  const filteredWarehouses = warehouses.filter(warehouse => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.bins.some(bin => 
        bin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bin.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    // Advanced filters
    const matchesWarehouseName = !filters.warehouseName || 
      warehouse.name.toLowerCase().includes(filters.warehouseName.toLowerCase()) ||
      warehouse.displayName?.toLowerCase().includes(filters.warehouseName.toLowerCase());

    const matchesWarehouseStatus = filters.warehouseStatus === 'all' || warehouse.status === filters.warehouseStatus;

    const matchesWarehouseCode = !filters.warehouseCode || 
      warehouse.code.toLowerCase().includes(filters.warehouseCode.toLowerCase());

    const matchesHasNoBins = !filters.hasNoBins || warehouse.bins.length === 0;

    const matchesBinStatus = filters.binStatus === 'all' || 
      warehouse.bins.some(bin => bin.status === (filters.binStatus as 'active' | 'inactive' | 'deprecated'));

    return matchesSearch && matchesWarehouseName && matchesWarehouseStatus && matchesWarehouseCode && matchesHasNoBins && matchesBinStatus;
  });

  // Handle sorting
  const handleSort = (field: 'name' | 'code' | 'bins') => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Apply sorting
  const sortedWarehouses = [...filteredWarehouses].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;

    let aValue: string | number = '';
    let bValue: string | number = '';

    if (sortField === 'name') {
      aValue = a.displayName || a.name;
      bValue = b.displayName || b.name;
    } else if (sortField === 'code') {
      aValue = a.code;
      bValue = b.code;
    } else if (sortField === 'bins') {
      aValue = a.bins.length;
      bValue = b.bins.length;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedWarehouses.length / itemsPerPage);
  const paginatedWarehouses = sortedWarehouses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setFilters({
      warehouseStatus: 'all',
      binStatus: 'all',
      warehouseCode: '',
      warehouseName: '',
      hasNoBins: false,
    });
    setCurrentPage(1);
    toast.success('Filters cleared');
  };

  const handleAddBin = () => {
    if (!selectedWarehouse) return;

    if (!binFormData.binName) {
      toast.error('Please enter a bin name');
      return;
    }

    if (!binFormData.binCode) {
      toast.error('Please enter a bin code');
      return;
    }

    const newBin: Bin = {
      id: `${selectedWarehouse.id}-${Date.now()}`,
      name: binFormData.binName,
      code: binFormData.binCode,
      status: binFormData.status,
    };

    const updatedWarehouses = warehouses.map(warehouse =>
      warehouse.id === selectedWarehouse.id
        ? { ...warehouse, bins: [...warehouse.bins, newBin] }
        : warehouse
    );

    setWarehouses(updatedWarehouses);
    setIsAddBinDialogOpen(false);
    setSelectedWarehouse(null);
    setBinFormData({ binName: '', binCode: '', status: 'active' });
    toast.success('Bin added successfully');
  };

  const confirmDelete = () => {
    if (!deletingItem || !deleteType) return;

    if (deleteType === 'bin') {
        const updatedWarehouses = warehouses.map(warehouse =>
        warehouse.id === deletingItem.warehouseId
            ? {
                ...warehouse,
                bins: warehouse.bins.filter(bin => bin.id !== deletingItem.id),
            }
            : warehouse
        );

        setWarehouses(updatedWarehouses);
        toast.success('Bin deleted successfully');
    }

    if (deleteType === 'warehouse') {
        const updatedWarehouses = warehouses.filter(
        warehouse => warehouse.id !== deletingItem.id
        );

        setWarehouses(updatedWarehouses);
        toast.success('Warehouse deleted successfully');
    }

    setIsDeleteDialogOpen(false);
    setDeletingItem(null);
    setDeleteType(null);
  };

  // Unused function - bin deletion is handled through the delete dialog
  // const handleDeleteBin = (warehouseId: string, binId: string) => {
  //   if (window.confirm('Are you sure you want to delete this bin?')) {
  //     const updatedWarehouses = warehouses.map(warehouse =>
  //       warehouse.id === warehouseId
  //         ? { ...warehouse, bins: warehouse.bins.filter(bin => bin.id !== binId) }
  //         : warehouse
  //     );
  //     setWarehouses(updatedWarehouses);
  //     toast.success('Bin deleted successfully');
  //   }
  // };

  // Removed handleDeleteWarehouse - warehouses cannot be deleted (pre-populated system data)

  const handleEditWarehouse = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setEditWarehouseFormData({
      selectedWarehouse: warehouse.name,
      name: warehouse.name,
      displayName: warehouse.displayName || '',
      code: warehouse.code,
      dialCode: warehouse.dialCode || '',
      warehouseCode: warehouse.warehouseCode || '',
      status: warehouse.status,
    });
    setIsEditWarehouseDialogOpen(true);
  };

  const handleUpdateWarehouse = () => {
    if (!editingWarehouse) return;

    const updatedWarehouses = warehouses.map(warehouse =>
      warehouse.id === editingWarehouse.id
        ? {
            ...warehouse,
            name: editWarehouseFormData.name,
            displayName: editWarehouseFormData.displayName || undefined,
            dialCode: editWarehouseFormData.dialCode,
            warehouseCode: editWarehouseFormData.warehouseCode,
            status: editWarehouseFormData.status,
          }
        : warehouse
    );

    setWarehouses(updatedWarehouses);
    setIsEditWarehouseDialogOpen(false);
    setEditingWarehouse(null);
    setEditWarehouseFormData({ selectedWarehouse: '', name: '', displayName: '', code: '', dialCode: '', warehouseCode: '', status: 'active' });
    toast.success('Warehouse updated successfully');
  };

  const handleEditBin = (warehouse: Warehouse, bin: Bin) => {
    setSelectedWarehouse(warehouse);
    setEditingBin(bin);
    setEditBinFormData({
      name: bin.name,
      code: bin.code,
      status: bin.status,
    });
    setIsEditBinDialogOpen(true);
  };

  const handleAddWarehouse = () => {
    if (!addWarehouseFormData.name || !addWarehouseFormData.code) {
        toast.error('Please fill all required fields');
        return;
    }

    const newWarehouse: Warehouse = {
        id: `warehouse-${Date.now()}`,
        name: addWarehouseFormData.name,
        code: addWarehouseFormData.code,
        dialCode: addWarehouseFormData.dialCode,
        warehouseCode: addWarehouseFormData.countryCode,
        status: addWarehouseFormData.status,
        bins: [], // no bins initially
    };

    setWarehouses(prev => [...prev, newWarehouse]);

    setIsAddWarehouseDialogOpen(false);
    setAddWarehouseFormData({
        name: '',
        code: '',
        dialCode: '',
        countryCode: '',
        status: 'active',
    });

    toast.success('Warehouse added successfully');
  };

  const handleUpdateBin = () => {
    if (!editingBin || !selectedWarehouse) return;

    if (!editBinFormData.code) {
      toast.error('Please enter a bin code');
      return;
    }

    const updatedWarehouses = warehouses.map(warehouse =>
      warehouse.id === selectedWarehouse.id
        ? {
            ...warehouse,
            bins: warehouse.bins.map(bin =>
              bin.id === editingBin.id
                ? {
                    ...bin,
                    code: editBinFormData.code,
                    status: editBinFormData.status,
                  }
                : bin
            )
          }
        : warehouse
    );

    setWarehouses(updatedWarehouses);
    setIsEditBinDialogOpen(false);
    setEditingBin(null);
    setSelectedWarehouse(null);
    setEditBinFormData({ name: '', code: '', status: 'active' });
    toast.success('Bin updated successfully');
  };



  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      active: 'bg-green-100 text-green-700 border-green-300',
      inactive: 'bg-gray-100 text-gray-700 border-gray-300',
      deprecated: 'bg-orange-100 text-orange-700 border-orange-300',
    };

    return (
      <Badge className={variants[status] || variants.active}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };



  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

          <Button
            onClick={() => setIsAddWarehouseDialogOpen(true)}
            style={{ backgroundColor: "#5B9BD5" }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Warehouse
          </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Active Warehouses</p>
                <p className="text-2xl font-bold text-green-900">
                  {warehouses.filter(c => c.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Active Bins</p>
                <p className="text-2xl font-bold text-emerald-900">
                  {warehouses.reduce((sum, warehouse) => 
                    sum + warehouse.bins.filter(bin => bin.status === 'active').length, 0
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Toggle */}
      <Card className="bg-white border-[#5B9BD5]/30">
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search warehouse or bin..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              style={showFilters ? { background: '#5B9BD5' } : {}}
              className={showFilters ? "text-white" : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"}
              title="Toggle Advanced Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Section */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter warehouses and bins by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Warehouse Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-warehouse-name" className="text-sm">Warehouse Name</Label>
                <Input
                  id="filter-warehouse-name"
                  placeholder="Search by warehouse name..."
                  value={filters.warehouseName}
                  onChange={(e) => setFilters({ ...filters, warehouseName: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              {/* Warehouse Code Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-warehouse-code" className="text-sm">Warehouse Code</Label>
                <Input
                  id="filter-warehouse-code"
                  placeholder="e.g., WH-KRB, WH-NJF"
                  value={filters.warehouseCode}
                  onChange={(e) => setFilters({ ...filters, warehouseCode: e.target.value.toUpperCase() })}
                  className="font-mono h-9 text-sm"
                  maxLength={10}
                />
              </div>

              {/* Warehouse Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-warehouse-status" className="text-sm">Warehouse Status</Label>
                <Select
                  value={filters.warehouseStatus}
                  onValueChange={(value: string) => setFilters({ ...filters, warehouseStatus: value as 'all' | 'active' | 'inactive' })}
                >
                  <SelectTrigger id="filter-warehouse-status" className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bin Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-bin-status" className="text-sm">Bin Status</Label>
                <Select
                  value={filters.binStatus}
                  onValueChange={(value: string) => setFilters({ ...filters, binStatus: value as 'all' | 'active' | 'inactive' | 'deprecated' })}
                >
                  <SelectTrigger id="filter-bin-status" className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Bins</SelectItem>
                    <SelectItem value="inactive">Inactive Bins</SelectItem>
                    <SelectItem value="deprecated">Deprecated Bins</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Has No Bins Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Special Filters</Label>
                <div className="flex items-center space-x-2 h-9 px-3 border rounded-md bg-white">
                  <input
                    type="checkbox"
                    id="filter-no-bins"
                    checked={filters.hasNoBins}
                    onChange={(e) => setFilters({ ...filters, hasNoBins: e.target.checked })}
                    className="w-4 h-4 text-[#5B9BD5] border-gray-300 rounded focus:ring-[#5B9BD5]"
                  />
                  <Label 
                    htmlFor="filter-no-bins"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Warehouses with no bins
                  </Label>
                </div>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button */}
            {(filters.warehouseName || filters.warehouseCode || filters.warehouseStatus !== 'all' || filters.binStatus !== 'all' || filters.hasNoBins) && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.warehouseName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Warehouse: {filters.warehouseName}
                      </Badge>
                    )}
                    {filters.warehouseCode && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Code: {filters.warehouseCode}
                      </Badge>
                    )}
                    {filters.warehouseStatus !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Warehouse: {filters.warehouseStatus}
                      </Badge>
                    )}
                    {filters.binStatus !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Bin: {filters.binStatus}
                      </Badge>
                    )}
                    {filters.hasNoBins && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        No Bins Only
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Warehouses Table - Desktop */}
      <Card className="bg-white border-[#5B9BD5]/30 hidden lg:block">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
          <CardTitle style={{ color: '#5B9BD5' }}>Warehouse & Bin</CardTitle>
          <CardDescription className="pb-6">
            {sortedWarehouses.length} Warehou{sortedWarehouses.length !== 1 ? 'ses' : 'se'} found
          </CardDescription>
        </CardHeader>
        <CardContent style={{ padding: "0px 0px 10px 0px" }}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-sky-50 to-blue-50">
                  <TableHead 
                    style={{ color: '#5B9BD5' }} 
                    className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Code
                      {sortField === 'name' ? (
                        sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    style={{ color: '#5B9BD5' }} 
                    className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('code')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortField === 'code' ? (
                        sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-6 py-4">Venue</TableHead>
                  <TableHead 
                    style={{ color: '#5B9BD5' }} 
                    className="px-6 py-4 cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('bins')}
                  >
                    <div className="flex items-center gap-1">
                      Bin
                      {sortField === 'bins' ? (
                        sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-6 py-4">Status</TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-6 py-4 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedWarehouses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 px-6 text-gray-500">
                      No warehouse found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedWarehouses.map((warehouse) => (
                    <TableRow key={warehouse.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-6 py-4">
                       <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] font-medium">
                          {warehouse.warehouseCode || warehouse.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                         <div className="flex items-center gap-3">
                          <div className="flex flex-col">
                            <span className="text-gray-900 font-medium">
                              {warehouse.displayName || warehouse.name}
                            </span>
                            {warehouse.displayName && (
                              <span className="text-xs text-gray-500">({warehouse.name})</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <span className="text-gray-700">{warehouse.dialCode || '-'}</span>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <div className="space-y-2">
                          {warehouse.bins.length === 0 ? (
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <p className="text-sm text-gray-500 italic">No bins added yet</p>
                            </div>
                          ) : (
                            warehouse.bins.map((bin) => (
                              <div key={bin.id} className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-sky-50 to-blue-50 rounded-lg border border-sky-200 hover:border-sky-300 transition-colors">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="flex flex-col flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                      {bin.displayName || bin.name}
                                    </span>
                                    {bin.displayName && (
                                      <span className="text-xs text-gray-500 truncate">({bin.name})</span>
                                    )}
                                  </div>
                                  <Badge variant="outline" className="text-xs border-[#5B9BD5]/30 text-[#5B9BD5] font-medium">
                                    {bin.code}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(bin.status)}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditBin(warehouse, bin)}
                                    className="text-[#5B9BD5] hover:bg-white h-8 w-8 p-0"
                                    title="Edit bin"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                        setDeleteType('bin');
                                        setDeletingItem({
                                            id: bin.id,
                                            name: bin.name,
                                            warehouseId: warehouse.id,
                                        });
                                        setIsDeleteDialogOpen(true);
                                    }}
                                    className="text-red-600 hover:bg-white h-8 w-8 p-0"
                                    title="Delete bin"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedWarehouse(warehouse);
                              setIsAddBinDialogOpen(true);
                            }}
                            className="w-full text-[#5B9BD5] hover:bg-sky-100 border border-dashed border-sky-300 py-2"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Bin to {warehouse.displayName || warehouse.name}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4">{getStatusBadge(warehouse.status)}</TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditWarehouse(warehouse)}
                            className="text-[#5B9BD5] hover:bg-sky-50"
                            title="Edit warehouse"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => {
                                setDeleteType('warehouse');
                                setDeletingItem({
                                    id: warehouse.id,
                                    name: warehouse.name,
                                });
                                setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div> 
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Controls - Desktop Only */}
      {totalPages > 1 && (
        <Card className="border-[#5B9BD5]/20 hidden lg:block">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedWarehouses.length)} of {sortedWarehouses.length} warehouses
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
  let page: number;

  if (totalPages <= 5) {
    page = i + 1;
  } else if (currentPage <= 3) {
    page = i + 1;
  } else if (currentPage >= totalPages - 2) {
    page = totalPages - 4 + i;
  } else {
    page = currentPage - 2 + i;
  }

  return (
    <Button
      key={page}
      variant={currentPage === page ? "default" : "outline"}
      size="sm"
      onClick={() => handlePageChange(page)}
      className={
        currentPage === page
          ? "h-8 w-8 p-0 bg-[#5B9BD5] text-white hover:bg-[#4A8BC4]"
          : "h-8 w-8 p-0"
      }
    >
      {page}
    </Button>
  );
})}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Warehouse Dialog */}
      <Dialog open={isEditWarehouseDialogOpen} onOpenChange={setIsEditWarehouseDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Edit Warehouse</DialogTitle>
            <DialogDescription>
              Update the warehouse details, code, and warehouse code
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editWarehouseName">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editWarehouseName"
                placeholder="e.g., Karbala Warehouse"
                value={editWarehouseFormData.name}
                onChange={(e) => setEditWarehouseFormData({ ...editWarehouseFormData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editDialCode">
                Venue <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editDialCode"
                placeholder="e.g., Karbala"
                value={editWarehouseFormData.dialCode}
                onChange={(e) => setEditWarehouseFormData({ ...editWarehouseFormData, dialCode: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Venue location (auto-filled when warehouse is selected)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editWarehouseCode">
                Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editWarehouseCode"
                placeholder="e.g., WH-KRB"
                value={editWarehouseFormData.warehouseCode}
                onChange={(e) => setEditWarehouseFormData({ ...editWarehouseFormData, warehouseCode: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editWarehouseStatus">Status <span className="text-red-500">*</span></Label>
              <Select
                value={editWarehouseFormData.status}
                onValueChange={(value: string) => setEditWarehouseFormData({ ...editWarehouseFormData, status: value as 'active' | 'inactive' })}
              >
                <SelectTrigger id="editWarehouseStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditWarehouseDialogOpen(false);
                setEditingWarehouse(null);
                setEditWarehouseFormData({ selectedWarehouse: '', name: '', displayName: '', code: '', dialCode: '', warehouseCode: '', status: 'active' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateWarehouse}
              disabled={!editWarehouseFormData.name || !editWarehouseFormData.dialCode || !editWarehouseFormData.warehouseCode}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              Update Warehouse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Add Warehouse Dialog */}
      <Dialog open={isAddWarehouseDialogOpen} onOpenChange={setIsAddWarehouseDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Add Warehouse</DialogTitle>
            <DialogDescription>
                Create a new warehouse with venue and code
            </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">

            {/* Name */}
            <div className="space-y-2">
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input
                placeholder="e.g., Karbala Warehouse"
                value={addWarehouseFormData.name}
                onChange={(e) =>
                    setAddWarehouseFormData({ ...addWarehouseFormData, name: e.target.value })
                }
                />
            </div>

            {/* Venue */}
            <div className="space-y-2">
                <Label>Venue <span className="text-red-500">*</span></Label>
                <Input
                placeholder="e.g., Karbala"
                value={addWarehouseFormData.dialCode}
                onChange={(e) =>
                    setAddWarehouseFormData({ ...addWarehouseFormData, dialCode: e.target.value })
                }
                />
            </div>

            {/* Code */}
            <div className="space-y-2">
                <Label>Code <span className="text-red-500">*</span></Label>
                <Input
                placeholder="e.g., WH-KRB"
                value={addWarehouseFormData.code}
                onChange={(e) =>
                    setAddWarehouseFormData({ ...addWarehouseFormData, code: e.target.value })
                }
                />
            </div>

            {/* Status */}
            <div className="space-y-2">
                <Label>Status</Label>
                <Select
                value={addWarehouseFormData.status}
                onValueChange={(value: string) =>
                    setAddWarehouseFormData({
                    ...addWarehouseFormData,
                    status: value as 'active' | 'inactive',
                    })
                }
                >
                <SelectTrigger>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
                </Select>
            </div>

            </div>

            <DialogFooter>
            <Button
                variant="outline"
                onClick={() => setIsAddWarehouseDialogOpen(false)}
            >
                Cancel
            </Button>

            <Button
                onClick={handleAddWarehouse}
                style={{ background: '#5B9BD5' }}
                className="text-white"
            >
                Add Warehouse
            </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Bin Dialog */}
      <Dialog open={isEditBinDialogOpen} onOpenChange={setIsEditBinDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Edit Bin</DialogTitle>
            <DialogDescription>
              Update the bin code and status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bin Name</Label>
              <Input
                value={editBinFormData.name}
                disabled
                className="bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">Bin name cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editBinCode">
                Bin Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="editBinCode"
                placeholder="e.g., BIN001"
                value={editBinFormData.code}
                onChange={(e) => setEditBinFormData({ ...editBinFormData, code: e.target.value.toUpperCase() })}
                maxLength={5}
              />
              <p className="text-xs text-gray-500">Internal reference code (2-5 characters)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editBinStatus">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={editBinFormData.status}
                onValueChange={(value: string) => setEditBinFormData({ ...editBinFormData, status: value as 'active' | 'inactive' | 'deprecated' })}
              >
                <SelectTrigger id="editBinStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditBinDialogOpen(false);
                setEditingBin(null);
                setSelectedWarehouse(null);
                setEditBinFormData({ name: '', code: '', status: 'active' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateBin}
              disabled={!editBinFormData.code}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              Update Bin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Bin Dialog */}
      <Dialog open={isAddBinDialogOpen} onOpenChange={setIsAddBinDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>
              Add Bin to {selectedWarehouse?.displayName || selectedWarehouse?.name}
            </DialogTitle>
            <DialogDescription>
              Enter the bin name and code to add to this warehouse
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="binName">
                Bin Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="binName"
                placeholder="e.g., Bin A1"
                value={binFormData.binName}
                onChange={(e) => setBinFormData({ ...binFormData, binName: e.target.value })}
              />
              <p className="text-xs text-gray-500">Enter the name of the bin</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="binCode">
                Bin Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="binCode"
                placeholder="e.g., BIN001"
                value={binFormData.binCode}
                onChange={(e) => setBinFormData({ ...binFormData, binCode: e.target.value.toUpperCase() })}
                maxLength={5}
              />
              <p className="text-xs text-gray-500">Internal reference code (2-5 characters)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="binStatus">Status</Label>
              <Select
                value={binFormData.status}
                onValueChange={(value: string) => setBinFormData({ ...binFormData, status: value as 'active' | 'inactive' | 'deprecated' })}
              >
                <SelectTrigger id="binStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {binFormData.binName && (
              <Card className="p-3 bg-blue-50/50 border-blue-200">
                <div className="flex items-start gap-2">
                  <Database className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-xs text-blue-800">
                    <strong>Preview:</strong> This bin will be saved as "{binFormData.binName}"
                  </div>
                </div>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddBinDialogOpen(false);
                setSelectedWarehouse(null);
                setBinFormData({ binName: '', binCode: '', status: 'active' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddBin}
              disabled={!binFormData.binName || !binFormData.binCode}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              Add Bin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {deleteType === 'bin' ? 'Delete Bin' : 'Delete Warehouse'} 
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{deletingItem?.name}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingItem(null);
              }}
            >
              No, Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Warehouse Cards */}

      {/* Mobile Pagination */}
      {totalPages > 1 && (
        <Card className="border-[#5B9BD5]/20 lg:hidden">
          <CardContent className="p-4">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="text-sm text-gray-600 text-center">
                Page {currentPage} of {totalPages} ({sortedWarehouses.length} warehouses)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={currentPage === page ? "h-8 w-8 p-0 bg-[#5B9BD5] text-white hover:bg-[#4A8BC4]" : "h-8 w-8 p-0"}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};